import { useState } from "react";
import { Reveal } from "@components/ui";

interface RightsCategory {
  title: string;
  subtitle: string;
  sections: {
    title: string;
    items: { label: string; description?: string }[];
  }[];
}

function AccordionItem({
  title,
  index,
  children,
  defaultOpen,
}: {
  title: string;
  index: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 ${
        isOpen
          ? "bg-white border-[#122d63]/15 shadow-[0_2px_16px_rgba(18,45,99,0.06)]"
          : "bg-white border-[#e9e8e6] hover:border-[#d0d0d0] hover:shadow-sm"
      }`}
    >
      <button
        className="w-full flex items-center gap-4 md:gap-5 p-4 md:p-6 text-left cursor-pointer"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <div
          className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center shrink-0 text-[14px] md:text-[16px] font-bold transition-all duration-300 ${
            isOpen
              ? "bg-[#122d63] text-white"
              : "bg-[#f6f6f9] text-[#62636c]"
          }`}
        >
          {String(index).padStart(2, "0")}
        </div>
        <span
          className={`flex-1 text-[16px] md:text-[20px] lg:text-[24px] font-semibold tracking-[-0.04em] leading-[1.3] transition-colors duration-200 ${
            isOpen ? "text-[#122d63]" : "text-[#1e1f24]"
          }`}
        >
          {title}
        </span>
        <div
          className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
            isOpen ? "bg-[#122d63] rotate-180" : "bg-[#f1f1f1]"
          }`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="transition-colors duration-200"
          >
            <path
              d="M3.5 5.25L7 8.75L10.5 5.25"
              stroke={isOpen ? "white" : "#1e1f24"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 md:px-6 pb-5 md:pb-6 pt-0">
            <div className="w-full h-px bg-[#f0efed] mb-5" />
            <div className="md:pl-[52px] lg:pl-[68px] text-[14px] md:text-[18px] lg:text-[20px] text-[#62636c] leading-[1.6] tracking-[-0.04em]">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const categories: RightsCategory[] = [
  {
    title: "Your Rights as a GASIS Customer",
    subtitle: "As a GASIS customer, you have the right to:",
    sections: [
      {
        title: "Safety & Service Quality",
        items: [
          {
            label: "Right to Safe Gas Supply",
            description:
              "GASIS ensures installations, inspections, and equipment meet Dubai Civil Defense and ESMA safety standards.",
          },
          {
            label: "Right to Emergency Response",
            description:
              "24/7 emergency support via 800-GASIS (42747) with guaranteed SLA (60-120 minutes depending on location).",
          },
          {
            label: "Right to Regular Maintenance & Inspections",
            description:
              "Annual safety checks, pressure testing, and network evaluations.",
          },
        ],
      },
      {
        title: "Transparent Billing & Fair Charges",
        items: [
          {
            label: "Right to Clear & Accurate Billing",
            description:
              "Bills include consumption, fees, VAT, meter readings, and dates.",
          },
          {
            label: "Right to Dispute Charges",
            description:
              "Billing issues may be disputed within 14 days; GASIS will review within 3-5 working days.",
          },
          {
            label: "Right to Refunds When Applicable",
            description:
              "Refunds for overpayments, credit balances, or account closures as per policy.",
          },
          {
            label: "Right to Receive Tariff Information",
            description:
              "All tariff changes are published on the website and communicated to customers.",
          },
        ],
      },
      {
        title: "Data Privacy & Confidentiality",
        items: [],
      },
      {
        title: "Complaints & Feedback",
        items: [],
      },
      {
        title: "Connection, Disconnection & Access",
        items: [],
      },
    ],
  },
  {
    title: "Your Responsibilities as a GASIS Customer",
    subtitle: "For safety, accuracy, and reliability, customers must:",
    sections: [
      {
        title: "Safety Responsibilities",
        items: [
          {
            label: "Ensure Proper Ventilation",
            description:
              "Especially in kitchens, mechanical rooms, and storage areas.",
          },
          {
            label: "Report Leaks Immediately",
            description:
              "Call 800-GASIS if you smell gas or suspect a leak.",
          },
          {
            label: "Allow Access for Inspections",
            description:
              "Technicians must be able to access meters, valves, and gas rooms.",
          },
          {
            label: "Use Certified Appliances Only",
            description:
              "ESMA-certified stoves, heaters, and regulators.",
          },
          {
            label: "Avoid Tampering",
            description:
              "Never modify or interfere with pipes, meters, valves, or regulators. Tampering may result in penalties, disconnection, and liability for damages.",
          },
        ],
      },
      {
        title: "Billing & Payments",
        items: [
          {
            label: "Pay Bills on Time",
            description:
              "Invoices must be paid within the due date to avoid late fees (AED 35) and disconnection.",
          },
          {
            label: "Provide Accurate Information",
            description:
              "Correct personal details and documents (tenancy contract, EJARI, etc.).",
          },
          {
            label: "Notify GASIS for Move-Out",
            description:
              "Submit move-out form at least 3 days before vacating the property.",
          },
        ],
      },
      {
        title: "Access & Equipment Care",
        items: [],
      },
      {
        title: "Communication & Updates",
        items: [],
      },
    ],
  },
  {
    title: "Commercial / F&B Customer Rights & Responsibilities",
    subtitle: "",
    sections: [
      {
        title: "Commercial Rights",
        items: [
          { label: "Priority emergency support" },
          { label: "Quarterly safety reports" },
          { label: "Dedicated business relationship manager" },
          { label: "Custom technical support & inspections" },
        ],
      },
      {
        title: "Commercial Responsibilities",
        items: [
          { label: "Daily leak checks" },
          { label: "Weekly hood & duct cleaning" },
          { label: "Ensure trained staff can shut emergency valves" },
          {
            label:
              "Install gas solenoid valves integrated with fire alarm",
          },
          { label: "Maintain clear access to kitchens & gas rooms" },
        ],
      },
    ],
  },
  {
    title: "Developer & Owner Association Responsibilities",
    subtitle: "",
    sections: [
      {
        title: "Developer & Owner Association Responsibilities",
        items: [
          {
            label:
              "Maintain mechanical rooms per Civil Defense guidelines",
          },
          { label: "Ensure ventilation & fire-rated access areas" },
          { label: "Provide site access to GASIS for maintenance" },
          { label: "Display emergency instructions in common areas" },
          {
            label:
              "Keep records of building modifications affecting gas infrastructure",
          },
          {
            label: "Allow quarterly audits & annual safety inspections",
          },
        ],
      },
    ],
  },
];

export default function RightsContentSection() {
  let globalIndex = 0;

  return (
    <section className="w-full bg-[#fefcfc] py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[900px] mx-auto flex flex-col gap-14 md:gap-20">
        {categories.map((category, catIdx) => {
          const startIdx = globalIndex;

          return (
            <div key={catIdx} className="flex flex-col gap-6 md:gap-8">
              {/* Category Header */}
              <Reveal direction="up">
                <div className="text-center">
                  <h2 className="text-[24px] md:text-[36px] lg:text-[48px] font-bold tracking-[-0.04em] text-[#122d63] font-albert-sans leading-[1.15]">
                    {category.title}
                  </h2>
                  {category.subtitle && (
                    <p className="mt-3 text-[14px] md:text-[18px] lg:text-[20px] tracking-[-0.04em] text-[#62636c] font-albert-sans">
                      {category.subtitle}
                    </p>
                  )}
                </div>
              </Reveal>

              {/* Accordions */}
              <div className="flex flex-col gap-3 md:gap-4">
                {category.sections.map((section, secIdx) => {
                  globalIndex++;
                  const idx = startIdx + secIdx + 1;

                  return (
                    <Reveal key={secIdx} direction="up" delay={secIdx <= 2 ? secIdx * 40 : 0}>
                      <AccordionItem
                        title={section.title}
                        index={idx}
                        defaultOpen={secIdx === 0}
                      >
                        {section.items.length > 0 ? (
                          <div className="flex flex-col gap-4">
                            {section.items.map((item, i) => (
                              <div key={i} className="flex flex-col gap-1">
                                <div className="flex items-start gap-3">
                                  <span className="w-2 h-2 min-w-2 rounded-full bg-[#122d63]/40 mt-2.5" />
                                  <span className="font-medium text-[#1e1f24]">
                                    {item.label}
                                  </span>
                                </div>
                                {item.description && (
                                  <p className="pl-5">
                                    {item.description}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="font-medium italic text-[#999]">
                            Content coming soon.
                          </p>
                        )}
                      </AccordionItem>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
